// ERP-5152 / Pricing Template V2.0 — the shared maths behind a pricing template.
//
// Deriving a Final Platform Price from a Base Price is a three-step process:
//
//   Step 1  Reverse-derive Platform Price (Before Markup) from Base Price.
//           Fees are now charged *on the Platform Price*, not added on top of
//           the Base Price, so the relationship inverts:
//             Platform Price - fees(Platform Price) = Base Price
//           The result is then rounded to the cent — see roundToCents.
//   Step 2  Apply the markup band the Before-Markup price falls into. The
//           markup is rounded to the cent too, so the Final price is the sum of
//           the two figures the breakdown prints.
//   Step 3  Apply the template's Final Result Rule (rounding).
//
// Every screen that evaluates a template — Add/Edit Pricing Template's Test
// Calculation, the Pricing Template list, Platform Pricing's bulk "Use Pricing
// Template" — goes through here so they cannot drift apart.
import { applyFinalResultRule } from './finalResultRule.js';

// ── Platform Fee ────────────────────────────────────────────────────────────
export const FEE_FLAT = 'Flat Amount';
export const FEE_PCT_PLATFORM = '% Platform Price';

// The two formula types offered by the "Add Platform Fee" dropdown.
export const FORMULA_TYPES = [FEE_FLAT, FEE_PCT_PLATFORM];

// fee_type as the API stores it: 1 = Flat Amount, 2 = % of Platform Price.
export const feeFormulaType = f =>
    f.fee_type === 1 ? FEE_FLAT
        : f.fee_type === 2 ? FEE_PCT_PLATFORM
            : (f.formula_type || FEE_PCT_PLATFORM);

// API fee rows -> the { formula_type, value, cap } shape the maths below uses.
export const normalizeFees = (fees = []) => (fees || []).map(f => ({
    ...f,
    platform_fee_name: f.platform_fee_name || '',
    formula_type: feeFormulaType(f),
    value: f.fee_value !== undefined ? String(f.fee_value) : String(f.value || ''),
    cap: f.cap_amount !== undefined ? (f.cap_amount ? String(f.cap_amount) : '') : String(f.cap || ''),
}));

// What a single fee costs at a given Platform Price. A percentage fee stops
// growing once it hits its cap, which is what makes Step 1 piecewise.
export const feeOn = (row, platformPrice) => {
    if (!row) return 0;
    if (row.formula_type === FEE_FLAT) return parseFloat(row.value) || 0;
    const pct = parseFloat(row.value) || 0;
    let fee = platformPrice * (pct / 100);
    const cap = parseFloat(row.cap);
    if (!isNaN(cap) && cap > 0) fee = Math.min(fee, cap);
    return fee;
};

export const totalFeesOn = (rows = [], platformPrice) =>
    (rows || []).reduce((sum, r) => sum + feeOn(r, platformPrice), 0);

// Every figure in the Test Calculation breakdown is shown to the cent, so a step
// that feeds the next one rounds to the cent before handing its result on:
// the number the user reads is the number the next step works from. Uses the
// same toFixed the display does rather than Math.round(n * 100) / 100, so the
// two can never disagree on a value sitting midway between cents.
const roundToCents = n => Number(Number(n).toFixed(2));

// ── Step 1 ──────────────────────────────────────────────────────────────────
// Find the Platform Price that leaves exactly `base` once its own fees are
// taken off it. Solved by bisection rather than algebraically: a capped
// percentage fee makes fees(P) piecewise, so there is no single closed form —
// and bisection works out on its own which fees are sitting at their cap.
export const derivePlatformPrice = (rows, base) => {
    if (!(base > 0)) return 0;
    let lo = base;
    let hi = Math.max(base * 100, base + 10000);
    for (let i = 0; i < 200; i++) {
        const mid = (lo + hi) / 2;
        const net = mid - totalFeesOn(rows, mid);
        if (net < base) lo = mid; else hi = mid;
    }
    return (lo + hi) / 2;
};

// ── Step 2: the Platform Price Markup ladder ────────────────────────────────
export const MARKUP_NONE = 'none';
export const MARKUP_FIXED = 'fixed';
export const MARKUP_PERCENT = 'percent';

// The leading blank is "not chosen yet" — an error state the user has to
// resolve, not a silent "No Markup". The picker surfaces it as the field's "×"
// clear rather than as an empty row in the menu.
export const MARKUP_TYPE_OPTIONS = [
    { value: '', label: '' },
    { value: MARKUP_NONE, label: 'No Markup' },
    { value: MARKUP_FIXED, label: 'Flat Amount' },
    { value: MARKUP_PERCENT, label: 'Percentage' },
];

// API markup rows -> { upper, includeUpper, type, param }.
export const normalizeMarkups = (markups = []) => (markups || []).map(m => ({
    upper: m.upper_bound != null && m.upper_bound !== '' ? Number(m.upper_bound) : null,
    includeUpper: !!m.upper_inclusive,
    type: m.markup_type || MARKUP_NONE,
    param: m.value != null && m.value !== '' ? Number(m.value) : null,
}));

// A band stores only its own upper bound. Its lower bound is the previous
// band's upper bound, and its lower inclusivity is the complement of that
// band's — exactly one of two adjacent bands owns the shared boundary value,
// which is why toggling one band's bracket flips its neighbour's.
export const markupBounds = (list, i) => {
    const r = list[i];
    if (i === 0) return { lower: 0, lowerInclusive: true, upper: r.upper, upperInclusive: r.includeUpper };
    const prev = list[i - 1];
    return { lower: prev.upper, lowerInclusive: !prev.includeUpper, upper: r.upper, upperInclusive: r.includeUpper };
};

// Which band a Platform Price falls into. Each boundary is tested with its own
// inclusivity, so a price sitting exactly on a boundary lands in the one band
// that owns it rather than in both or neither.
export const findMarkupBand = (list = [], price) => {
    for (let i = 0; i < list.length; i++) {
        const b = markupBounds(list, i);
        const lower = b.lower == null ? 0 : b.lower;
        const lowerOk = b.lowerInclusive ? price >= lower : price > lower;
        const upperOk = b.upper == null ? true : (b.upperInclusive ? price <= b.upper : price < b.upper);
        if (lowerOk && upperOk) return list[i];
    }
    return null;
};

export const markupAmount = (band, price) => {
    if (!band || !band.type || band.type === MARKUP_NONE) return 0;
    const v = band.param == null ? 0 : band.param;
    if (band.type === MARKUP_FIXED) return v;
    return price * (v / 100);
};

// ── Display helpers ─────────────────────────────────────────────────────────
// A band's lower bound is its predecessor's upper bound, so it reads as "-"
// while that predecessor is still blank. The first band's lower bound is a
// real 0, not a missing value, so it still formats as 0.00.
export const fmtBound = n => (n == null || isNaN(n) ? '-' : Number(n).toFixed(2));

// "SGD [0.00, 160.00)" — the last band's open end renders as ∞.
export const formatMarkupRange = (list, i) => {
    const b = markupBounds(list, i);
    const open = b.lowerInclusive ? '[' : '(';
    if (b.upper == null) return `SGD ${open}${fmtBound(b.lower)}, ∞)`;
    const close = b.upperInclusive ? ']' : ')';
    return `SGD ${open}${fmtBound(b.lower)}, ${fmtBound(b.upper)}${close}`;
};

export const formatMarkupValue = band => {
    if (!band || !band.type || band.type === MARKUP_NONE || band.param == null) return '-';
    if (band.type === MARKUP_FIXED) return `SGD ${Number(band.param).toFixed(2)}`;
    return `${parseFloat(Number(band.param).toFixed(5))}%`;
};

// ── All three steps ─────────────────────────────────────────────────────────
// `rows` and `markups` must already be in normalized shape. Returns every
// intermediate figure the Test Calculation breakdown shows, so callers that
// only want the final price can read `finalPrice` and ignore the rest.
export const calculatePlatformPrice = (rows, markups, base, finalResultRule) => {
    // Bisection returns far more precision than the breakdown shows — 490.2857…
    // behind a displayed 490.29. Round to the cent here, before anything reads
    // it: the fee lines, the markup band the price falls into and the Markup
    // step all work from the figure on screen. Band choice is the one that bites
    // — a raw 490.2857… sits below a band boundary at 490.29 that the displayed
    // price sits on.
    const beforeMarkup = roundToCents(derivePlatformPrice(rows, base));
    const band = findMarkupBand(markups, beforeMarkup);
    // Rounded for the same reason: a percentage band gives 490.29 x 23% =
    // 112.7667, printed as 112.77. Adding the unrounded figure would leave the
    // Markup step showing two numbers that do not add up to its own total.
    const markupAmt = roundToCents(markupAmount(band, beforeMarkup));
    const finalPrice = applyFinalResultRule(beforeMarkup + markupAmt, finalResultRule);

    // Profit is measured against the *final* (rounded) price, since that is
    // what the platform actually charges the buyer and takes its cut of. The fee
    // lines are rounded to the cent before they are subtracted, so the Expected
    // Profit panel comes to the same total a reader gets subtracting the figures
    // it prints.
    const finalFees = (rows || []).map(r => ({ name: r.platform_fee_name || 'Fee', amount: roundToCents(feeOn(r, finalPrice)) }));
    const profit = roundToCents(finalPrice - finalFees.reduce((s, f) => s + f.amount, 0) - base);

    return {
        base,
        beforeMarkup,
        // Display-only, but rounded on the same principle as the rest: every
        // figure the breakdown prints is the figure its arithmetic used.
        reverseFees: (rows || []).map(r => ({ name: r.platform_fee_name || 'Fee', amount: roundToCents(feeOn(r, beforeMarkup)) })),
        band,
        markupAmt,
        finalPrice,
        finalFees,
        profit,
    };
};
